import { useEffect } from "react";

// A modal dialog or TinyMCE menu is on screen — Escape should close that first.
const isBlockingOverlayOpen = () =>
  Boolean(document.querySelector(".MuiDialog-container, .tox-dialog, .tox-menu"));

// Blur an active text editor inside the container so it commits via its onBlur.
const blurActiveEditableWithin = (container) => {
  const active = document.activeElement;
  if (
    active &&
    container?.contains?.(active) &&
    (active.isContentEditable || active.tagName === "INPUT" || active.tagName === "TEXTAREA")
  ) {
    active.blur();
  }
};

/**
 * Escape peels document selection one layer at a time:
 *   1st Escape — clears an inner product/service row selection.
 *   2nd Escape — deselects the layout item, blurring any active text editor
 *                first (its onBlur saves the content).
 * No-op while a dialog or TinyMCE menu is open so Escape closes those first.
 *
 * @param {Object}   params
 * @param {HTMLElement} [params.container]      element whose editables get blurred
 * @param {string|null} params.selectedRowId    currently selected product/service row
 * @param {Function} params.setSelectedRowId    clears the row selection
 * @param {string|null} params.selectedLayoutItem currently selected layout item
 * @param {Function} params.setSelectedLayoutItem clears the layout item selection
 */
const useLayeredEscapeDeselect = ({
  container,
  selectedRowId,
  setSelectedRowId,
  selectedLayoutItem,
  setSelectedLayoutItem
}) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Escape" || e.repeat) return;
      if (isBlockingOverlayOpen()) return;

      // Layer 1: inner product/service row.
      if (selectedRowId) {
        setSelectedRowId(null);
        return;
      }

      // Layer 2: the selected layout item.
      if (selectedLayoutItem) {
        blurActiveEditableWithin(container);
        setSelectedLayoutItem(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [container, selectedRowId, setSelectedRowId, selectedLayoutItem, setSelectedLayoutItem]);
};

export default useLayeredEscapeDeselect;
